<ng-container *ngIf="context() as reviewContext">
  <button *ngIf="collapsed()" type="button" class="health-review-launcher" aria-label="Open event health assistant" (click)="toggleCollapsed()">
    <mat-icon>health_and_safety</mat-icon>
    <span>{{ session.remainingCount() }}</span>
  </button>

  <aside *ngIf="!collapsed()" class="health-review-assistant" cdkDrag [cdkDragConstrainPosition]="constrainDrag" aria-label="Event health assistant">
    <header class="assistant-header">
      <div class="assistant-header__identity" cdkDragHandle title="Drag to move">
        <span class="assistant-logo"><mat-icon>health_and_safety</mat-icon></span>
        <div>
          <span class="assistant-kicker">{{ panelKicker() }}</span>
          <h2>{{ reviewContext.eventType }}</h2>
          <span class="assistant-review-level">
            <mat-icon aria-hidden="true">tune</mat-icon>
            {{ reviewLevelLabel() }}
          </span>
        </div>
      </div>
      <button type="button" class="icon-button" aria-label="Collapse health assistant" (click)="toggleCollapsed()">
        <mat-icon>chevron_right</mat-icon>
      </button>
    </header>

    <div *ngIf="automaticFindings().length" class="automatic-summary automatic-summary--top" role="status">
      <mat-icon>auto_fix_high</mat-icon>
      <span>
        <strong>Save will automatically update {{ automaticFindings().length }} {{ automaticFindings().length === 1 ? 'finding' : 'findings' }}</strong>
        <small>No additional action is required.</small>
      </span>
    </div>

    <section class="assistant-progress" *ngIf="findings().length || !assessmentNotice()">
      <div>
        <span>{{ progressLabel() }}</span>
        <strong>{{ progress() }}%</strong>
      </div>
      <div class="assistant-progress__track"><span [style.width.%]="progress()"></span></div>
    </section>

    <section class="assistant-findings">
      <p *ngIf="session.navigationNotice()" class="text-secondary px-4 text-sm" role="status">{{ session.navigationNotice() }}</p>
      <p *ngIf="assessmentNotice()" class="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-100" role="status">
        {{ assessmentNotice() }}
      </p>
      <nav *ngIf="showResolutionFilter()" class="resolution-filter" aria-label="Filter findings by resolution">
        <button
          *ngFor="let group of resolutionGroups"
          type="button"
          [class.resolution-filter__button--active]="activeResolutionGroup() === group.id"
          [disabled]="resolutionGroupCounts()[group.id] === 0"
          [attr.aria-pressed]="activeResolutionGroup() === group.id"
          (click)="selectResolutionGroup(group.id)"
        >
          <mat-icon>{{ group.icon }}</mat-icon>
          <span>{{ group.label }}</span>
          <strong>{{ resolutionGroupCounts()[group.id] }}</strong>
        </button>
      </nav>
      <div *ngIf="visibleFindings().length && resolutionSummary() as summary" class="resolution-summary" [ngClass]="'resolution-summary--' + summary.tone" role="status">
        <mat-icon>{{ summary.icon }}</mat-icon>
        <span>
          <strong>{{ summary.title }}</strong>
          <small>{{ summary.detail }}</small>
        </span>
      </div>
      <div class="assistant-findings__heading">
        <span>{{ findingsHeading() }}</span>
        <span>{{ visibleFindings().length }}</span>
      </div>
      <p *ngIf="session.warningCount()" class="text-secondary px-4 text-xs">
        {{ session.blockingCount() }} blocking errors · {{ session.warningCount() }} warnings
      </p>

      <div *ngIf="!findings().length && !assessmentNotice()" class="assistant-all-clear">
        <mat-icon>{{ reviewContext.status === 'ready' ? 'check_circle' : 'info' }}</mat-icon>
        <div>
          <strong>{{ emptyState().title }}</strong>
          <small>{{ emptyState().detail }}</small>
        </div>
      </div>

      <article
        *ngFor="let finding of visibleFindings(); let index = index"
        class="assistant-finding"
        [class.assistant-finding--actionable]="findingKind(finding) === 'form'"
        [class.assistant-finding--informational]="findingKind(finding) !== 'form'"
        [class.assistant-finding--active]="findingKind(finding) === 'form' && session.activeFindingId() === finding.id"
        [attr.role]="findingKind(finding) === 'form' ? 'button' : 'group'"
        [attr.tabindex]="findingKind(finding) === 'form' ? 0 : null"
        [class.assistant-finding--changed]="session.isLocallyChanged(finding.id) || session.isResolved(finding.id)"
        (click)="selectFinding(finding)"
        (keydown.enter)="selectFinding(finding)"
        (keydown.space)="selectFinding(finding); $event.preventDefault()"
      >
        <span class="finding-icon" [ngClass]="session.isResolved(finding.id) || session.isLocallyChanged(finding.id) ? 'finding-icon--updated' : 'finding-icon--' + finding.severity">
          <mat-icon *ngIf="session.isResolved(finding.id) || session.isLocallyChanged(finding.id); else findingNumber">check_circle</mat-icon>
          <ng-template #findingNumber><span [attr.aria-label]="'Finding ' + (index + 1)">{{ index + 1 }}</span></ng-template>
        </span>
        <span class="finding-body">
          <span class="finding-heading">
            <strong>{{ finding.title }}</strong>
            <span class="finding-severity" [ngClass]="session.isLocallyChanged(finding.id) ? 'finding-severity--updated' : 'finding-severity--' + finding.severity" [attr.aria-label]="session.isLocallyChanged(finding.id) ? 'Updated, pending save' : 'Severity: ' + finding.severity">
              {{ session.isLocallyChanged(finding.id) ? 'Updated' : (finding.severity | titlecase) }}
            </span>
          </span>
          <small>{{ finding.help || finding.message }}</small>

          <span class="finding-action" [ngClass]="session.isLocallyChanged(finding.id) ? 'finding-action--updated' : 'finding-action--' + findingKind(finding)">
            <mat-icon>{{ session.isLocallyChanged(finding.id) ? 'check' : findingKind(finding) === 'form' ? 'edit' : findingKind(finding) === 'linked' ? 'link' : findingKind(finding) === 'generated' ? 'save' : findingKind(finding) === 'managed' ? 'settings' : 'support_agent' }}</mat-icon>
            {{ session.isLocallyChanged(finding.id) ? 'Updated · pending save' : findingKindLabel(finding) }}
          </span>

          <span *ngIf="finding.suggestions.length && !session.isLocallyChanged(finding.id)" class="finding-suggestions">
            <span *ngFor="let item of finding.suggestions" class="suggestion-card" [ngClass]="'suggestion-card--' + finding.severity">
              <span>
                <small>Suggested from {{ item.source }}</small>
                <strong>{{ item.label }}</strong>
                <small>{{ item.reason }}</small>
              </span>
              <button type="button" (click)="applySuggestion(finding, item, $event)">Apply</button>
            </span>
          </span>

          <button *ngIf="session.isLocallyChanged(finding.id) && !finding.target.sectionOnly" type="button" class="restore-original" (click)="restoreOriginalValue(finding, $event)">
            <mat-icon>undo</mat-icon>
            Restore original
          </button>
          <span *ngIf="!session.isResolved(finding.id) && findingKind(finding) === 'form'" class="text-secondary mt-2 text-xs leading-relaxed">
            {{ finding.target.label }}
          </span>
        </span>
        <!-- Technical details temporarily hidden. Keep the drawer for later restoration.
        <details class="assistant-details" (click)="$event.stopPropagation()" (keydown)="$event.stopPropagation()">
          <summary [attr.aria-label]="'Technical details for ' + finding.title">
            <mat-icon aria-hidden="true">info_outline</mat-icon>
            <span>Technical details</span>
            <mat-icon class="assistant-details__chevron" aria-hidden="true">expand_more</mat-icon>
          </summary>
          <div class="assistant-details__content">
            <div class="assistant-details__explanation">
              <strong>Explanation</strong>
              <p>{{ errorExplanation(finding) }}</p>
            </div>
            <dl>
              <dt>Rule code</dt>
              <dd>{{ finding.ruleId }}</dd>
              <dt>Scope</dt>
              <dd>{{ scopeLabel(finding.scope) }}</dd>
              <ng-container *ngIf="finding.target.path">
                <dt>Data path</dt>
                <dd>{{ finding.target.path }}</dd>
              </ng-container>
              <ng-container *ngIf="finding.reference">
                <dt>Linked record</dt>
                <dd>{{ finding.reference }}</dd>
              </ng-container>
            </dl>
          </div>
        </details>
        -->
      </article>
    </section>

    <footer class="assistant-footer">
      <div *ngIf="activeResolutionGroup() === 'form' && actionableFindings().length" class="finding-navigation">
        <button type="button" aria-label="Previous finding" (click)="previousFinding()"><mat-icon>arrow_upward</mat-icon></button>
        <button type="button" aria-label="Next finding" (click)="nextFinding()"><mat-icon>arrow_downward</mat-icon></button>
      </div>
      <button type="button" class="exit-review" (click)="runPrimaryAction()">
        <mat-icon>{{ primaryAction() === 'submit' ? 'south' : 'arrow_back' }}</mat-icon>
        {{ primaryAction() === 'submit' ? 'Go to Submit' : 'Back to Health' }}
      </button>
    </footer>
  </aside>
</ng-container>
